import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { CrossDomainStorageService } from 'src/app/core/services/cross-domain-storage.service';
import { TOKEN_NAME, UNIDADE_KEY } from 'src/app/shared/services/session.service';
import { InactiveCheckService, AuthApiService } from 'src/app/core/services';
import { BACKGROUND_REQUEST, SUPPRESS_IDLE_RESET } from 'src/app/core/interceptor/background-request.context';
// import { AuthService } from 'src/app/core/services';

const SESSION_ENDED_DETAILS = ['Token inválido.', 'Sessão expirada.'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private storageService: CrossDomainStorageService,
    private inactiveCheckService: InactiveCheckService,
    private injector: Injector
    // private authService: AuthService
  ) { }

  /**
   * Limpeza única de sessão encerrada: choke point de TODA requisição. Em
   * 401 "Sessão expirada."/"Token inválido." limpa token, cache de sessão e
   * o inactive-checker (via AuthApiService, resolvido lazy p/ evitar ciclo
   * de DI) e navega ao login — garante que nenhum estado de auth obsoleto
   * sobreviva, mesmo que a chamada não passe pelo handleError do serviço.
   */
  private handleSessionEnded<T>(
    source: Observable<T>,
    request: HttpRequest<any>
  ): Observable<T> {
    return source.pipe(
      catchError((error) => {
        const detail = error?.error?.detail;
        if (error?.status === 401
          && SESSION_ENDED_DETAILS.includes(detail)
          && this.belongsToCurrentSession(request)) {
          this.injector.get(AuthApiService).expireSession();
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Só encerra a sessão se o 401 pertence ao token que continua ativo.
   *
   * Após a expiração por inatividade, várias requisições do token antigo podem
   * responder fora de ordem. Se o usuário já fez login novamente, uma dessas
   * respostas tardias não pode apagar o token novo — era exatamente a corrida
   * em que o login ficava preso até a página ser atualizada.
   */
  private belongsToCurrentSession(request: HttpRequest<any>): boolean {
    const authorization = request.headers.get('Authorization');
    const currentToken = this.storageService.getItemAsync(TOKEN_NAME);

    if (!authorization || !currentToken) {
      return false;
    }

    const separator = authorization.indexOf(' ');
    const requestToken = separator >= 0
      ? authorization.slice(separator + 1).trim()
      : authorization.trim();

    return requestToken === currentToken;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const isBackground = req.context.get(BACKGROUND_REQUEST);
    const suppressIdleReset = req.context.get(SUPPRESS_IDLE_RESET);

    // Polling em background não conta como atividade. O keepalive explícito
    // (suppressIdleReset) renova no backend mas só reseta o timer local após
    // o 204 (feito no success handler do keepAliveNow), não aqui no envio.
    if (!isBackground && !suppressIdleReset) {
      this.inactiveCheckService.updateLastUserAction();
    }

    let reqUrl = req.url;
    if ((environment.production) || (window.location.protocol === 'file:')) {
      reqUrl = environment.urlAPI + req.url;
    }

    let headers = req.headers.set('x-host', environment.urlBase);
    if (isBackground) {
      // Backend também ignora requisições background ao renovar a sessão.
      headers = headers.set('X-Background-Request', 'true');
    }

    let reqClone = req.clone({
      url: reqUrl,
      headers
    });
    if (reqClone.headers.has('no-xhrbackend')) {
      reqClone = reqClone.clone({ headers: reqClone.headers.delete('no-xhrbackend') });
      // console.log('Removido xhr-backend', reqClone);
      return this.handleSessionEnded(next.handle(reqClone), reqClone);
    }

    const unitKey = this.storageService.getItemAsync(UNIDADE_KEY);
    if (!!unitKey) {
      reqClone = reqClone.clone({ headers: reqClone.headers.set('Operation-Unit', unitKey) });
    }

    if (reqClone.headers.has('no-authorization')) {
      reqClone = reqClone.clone({ headers: reqClone.headers.delete('no-authorization') });
    } else {
      if (!reqClone.headers.has('Authorization')) {
        const tokenKey = this.storageService.getItemAsync(TOKEN_NAME);
        if (tokenKey && tokenKey !== '') {
          reqClone = reqClone.clone({ headers: reqClone.headers.set('Authorization', `Token ${tokenKey}`) });
        }
        // return this.authService.token.pipe(
        //   map(token => {
        //     return reqClone.clone({
        //       headers: reqClone.headers.set('Authorization', `JWT ${token}`)
        //     });
        //   }),
        //   mergeMap(data => next.handle(data))
        // );
      }
    }

    // console.log('intercept-end', reqClone);
    return this.handleSessionEnded(next.handle(reqClone), reqClone);
  }
}
